import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';

interface SolarPVCostData {
  chartData: any;
  chartOptions: any;
  narrative?: string;
  avgResidentialCost?: number;
  avgCommercialCost?: number;
}

// Helper function to get geofips for a location
async function getGeofipsForLocation(locationName: string): Promise<number | null> {
  try {
    return await supabaseLocationService.getGeofips(locationName);
  } catch (error) {
    console.error('Error getting geofips for location:', locationName, error);
    return null;
  }
}

export default async function fetchSolarPVCostSupabase(location: string): Promise<SolarPVCostData> {
  try {

    // Get geofips for the location
    const geofips = await getGeofipsForLocation(location);
    
    if (!geofips) {
      // Return placeholder data if no geofips found
      return {
        chartData: {
          labels: ['2019', '2020', '2021', '2022', '2023'],
          datasets: [{
            label: 'Residential Cost per Watt ($)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#1e3a8a',
            backgroundColor: 'rgba(30, 58, 138, 0.1)',
            fill: false
          }, {
            label: 'Commercial Cost per Watt ($)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: false
          }, {
            label: 'Utility Cost per Watt ($)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            fill: false
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Cost per Watt by Sector - ${location} (No Geofips Found)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Cost per Watt ($)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No geofips found for ${location} in the population database. This location may not be available in the solar installation database.`,
        avgResidentialCost: 0,
        avgCommercialCost: 0
      };
    }

    // Query Supabase by geoid for server-side filtering
    let { data: pvData, error } = await supabaseClient
      .from('all_pv_cost')
      .select('*')
      .eq('geoid', geofips)
      .order('year', { ascending: true });

    if (error) {
      console.error('Error fetching Solar PV Cost data:', error);
      throw error;
    }

    // Fallback: if no rows by geoid, try location_name (case-insensitive)
    if (!pvData || pvData.length === 0) {
      const { data: byLocation, error: locErr } = await supabaseClient
        .from('all_pv_cost')
        .select('*')
        .ilike('location_name', `%${location}%`)
        .order('year', { ascending: true });
      if (locErr) {
        console.error('Error fetching Solar PV Cost by location_name:', locErr);
      } else if (byLocation && byLocation.length > 0) {
        pvData = byLocation;
      }
    }


    if (!pvData || pvData.length === 0) {
      // Return placeholder data if no records found
      return {
        chartData: {
          labels: ['2019', '2020', '2021', '2022', '2023'],
          datasets: [{
            label: 'Residential Cost per Watt ($)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#1e3a8a',
            backgroundColor: 'rgba(30, 58, 138, 0.1)',
            fill: false
          }, {
            label: 'Commercial Cost per Watt ($)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#10b981',
            backgroundColor: 'rgba(16, 185, 129, 0.1)',
            fill: false
          }, {
            label: 'Utility Cost per Watt ($)',
            data: [0, 0, 0, 0, 0],
            borderColor: '#f59e0b',
            backgroundColor: 'rgba(245, 158, 11, 0.1)',
            fill: false
          }]
        },
        chartOptions: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: `Solar PV Cost per Watt by Sector - ${location} (No Data Available)`,
              font: { size: 16, weight: 'bold' as const }
            },
            legend: {
              position: 'top' as const,
              labels: {
                usePointStyle: true,
                padding: 20
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Year'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Cost per Watt ($)'
              },
              beginAtZero: true
            }
          }
        },
        narrative: `No solar PV cost data available for ${location}. This may indicate that the location has not yet been added to the solar installation database, or that solar installations are not yet recorded for this area.`,
        avgResidentialCost: 0,
        avgCommercialCost: 0
      };
    }

    // Process the data to create chart datasets (like the HTML version)
    const allYears = Array.from(new Set(pvData.map(item => Number(item.year)))).sort((a: number, b: number) => a - b);
    const years = allYears.filter(year => year <= 2024); // Filter to 2024 and earlier like HTML
    // Normalize sector values to lower-case for grouping
    const pvRows = pvData || [];
    const allSectors = Array.from(new Set(pvRows.map(item => String(item.sector || '').toLowerCase()))).filter(Boolean);
    // Filter to only Residential and Commercial
    const sectors = allSectors.filter(sector => 
      sector === 'residential' || sector === 'commercial'
    );
    
    const datasets = sectors.map((sector, index) => {
      const colors = ['#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6', '#06b6d4'];
      const color = colors[index % colors.length];
      
      const data = years.map(year => {
        const yearData = pvRows.filter(item => 
          Number(item.year) === year && String(item.sector || '').toLowerCase() === sector
        );
        if (yearData.length === 0) return 0;
        
        // Use the cost_per_watt column directly from the table (skip null/NaN)
        const costs = yearData
          .map(item => Number(item.cost_per_watt))
          .filter(v => Number.isFinite(v) && v > 0);
        const avgCostPerWatt = costs.length > 0 ? costs.reduce((sum, v) => sum + v, 0) / costs.length : 0;
        return avgCostPerWatt;
      });

      return {
        label: sector.charAt(0).toUpperCase() + sector.slice(1),
        data: data,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1,
        fill: false,
        tension: 0.1
      };
    });

    const chartData = {
      labels: years,
      datasets: datasets
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Solar PV Cost per Watt by Sector - ${location}`,
          font: { size: 16, weight: 'bold' as const }
        },
        legend: {
          position: 'top' as const,
          labels: {
            usePointStyle: true,
            padding: 20
          }
        }
      },
      scales: {
        x: {
          title: {
            display: true,
            text: 'Year'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Cost per Watt ($)'
          },
          beginAtZero: true
        }
      }
    };

    // Calculate average costs for narrative using the cost_per_watt column
    const allCosts = pvData.map(item => ({
      sector: String(item.sector || '').toLowerCase(),
      costPerWatt: Number(item.cost_per_watt) || 0,
      year: Number(item.year)
    })).filter(item => item.costPerWatt > 0);

    const resCosts = allCosts.filter(item => item.sector === 'residential');
    const avgResidentialCost = resCosts.length > 0 ? resCosts.reduce((sum, item) => sum + item.costPerWatt, 0) / resCosts.length : 0;

    const comCosts = allCosts.filter(item => item.sector === 'commercial');
    const avgCommercialCost = comCosts.length > 0 ? comCosts.reduce((sum, item) => sum + item.costPerWatt, 0) / comCosts.length : 0;

    const narrative = `${location} shows varying solar PV costs across sectors. Residential installations average $${avgResidentialCost.toFixed(2)} per watt, and commercial installations average $${avgCommercialCost.toFixed(2)} per watt. These cost variations reflect different installation scales, complexity, and market conditions across sectors.`;

    return {
      chartData,
      chartOptions,
      narrative,
      avgResidentialCost,
      avgCommercialCost
    };

  } catch (error) {
    console.error('Error in fetchSolarPVCostSupabase:', error);
    throw error;
  }
}
